import React, { useEffect, useRef, useState } from "react";
import { Alert, Button } from "reactstrap";
import { useHistory, Link } from "react-router-dom";
import MyCollectionLayout from "./MyCollectionLayout";
import McBackLink from "./McBackLink";
import McUploadProgress from "./McUploadProgress";
import { createExternalItem, isCollectorSessionError, updateCollectorItem } from "../../services/collectorApi";
import { durableMediaWrites } from "../../media/mediaReferences";
import { collectorMongoId } from "../../helpers/collectorIds";
import { assertCollectorImage, uploadCollectorPhoto } from "../../helpers/collectorUpload";
import {
  COLLECTOR_PHOTO_EXPIRED_MESSAGE,
  collectorPhotoProblem,
  isPhotoExpiredError,
} from "../../media/collectorMediaUpload";
import { collectorLoginPath, getCollectorToken } from "../../helpers/collectorSession";
import { UNTITLED_PIECE_TITLE } from "./collectorPieceForm";

/** Photos a quick add takes before the person moves on; more can be added on the piece page. */
export const QUICK_ADD_PHOTO_CAP = 5;

type Photo = {
  key: string;
  preview: string;
  url?: string;
  percent: number;
  failed?: boolean;
};

type Saved = { id: string; title: string };

let photoSeq = 0;

/** One id per "add a piece" attempt, so a retry after a dropped connection never saves it twice. */
export const newClientRequestId = (): string => {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `qa-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const PLAN_LIMIT = "PLAN_LIMIT_EXTERNAL_ITEMS";
/** The id was used by a piece that has since been archived: this save needs a fresh id. */
const CLIENT_REQUEST_ARCHIVED = "CLIENT_REQUEST_ARCHIVED";

/** HTTP status phrases and transport errors: true, but they tell the person nothing they can act on. */
const GENERIC_SERVER_TEXT =
  /^(?:\d{3}\s+)?(?:bad request|unauthori[sz]ed|forbidden|not found|method not allowed|conflict|gone|payload too large|unsupported media type|too many requests|internal server error|not implemented|bad gateway|service unavailable|gateway time-?out)\.?$/i;
const TECHNICAL_TEXT = /(?:exception|status code|network error|timeout of|stack|null|undefined|\bat\s+\w+\.\w+)/i;

/** Server text worth showing as is: a sentence, not an error code, an HTTP status phrase or a stack. */
export const readableServerMessage = (msg?: string): string => {
  const text = (msg || "").trim();
  if (!text || !/\s/.test(text) || text.length > 200) return "";
  if (GENERIC_SERVER_TEXT.test(text) || TECHNICAL_TEXT.test(text)) return "";
  return text;
};

/** A photo address as the server keeps it: the media reference when known, else the address without its signature. */
const samePhoto = (url?: string | null): string => {
  const durable = durableMediaWrites({ coverPhotoUrl: url || "" }).coverPhotoUrl as string;
  return durable.startsWith("hkp-media:") ? durable : durable.split("?")[0];
};

type DraftBody = { title?: string; coverPhotoUrl?: string; additionalImageUrls: string[] };

/**
 * When the server answers with the piece an earlier attempt of this draft already saved (the reply to
 * that attempt was lost), the name or photos may have changed since. Returns what to update on it,
 * or null when it already matches.
 */
export const draftChanges = (sent: DraftBody, item: Record<string, any>): Record<string, unknown> | null => {
  const changes: Record<string, unknown> = {};
  const title = sent.title || UNTITLED_PIECE_TITLE;
  if ((item.title || UNTITLED_PIECE_TITLE).trim() !== title) changes.title = title;
  const photosNow = [sent.coverPhotoUrl, ...sent.additionalImageUrls].map(samePhoto);
  const photosThen = [item.coverPhotoUrl, ...(Array.isArray(item.additionalImageUrls) ? item.additionalImageUrls : [])].map(samePhoto);
  if (photosNow.join("\n") !== photosThen.join("\n")) {
    changes.coverPhotoUrl = sent.coverPhotoUrl;
    changes.additionalImageUrls = sent.additionalImageUrls;
  }
  return Object.keys(changes).length ? changes : null;
};

const serverMessage = (err: unknown): string | undefined =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message;

/**
 * Quick add: one photo saves a piece. A name is optional (the server titles it UNTITLED_PIECE_TITLE),
 * and everything else is added later on the piece page.
 */
const AddExternalPiece: React.FC = () => {
  const history = useHistory();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [planLimit, setPlanLimit] = useState(false);
  const [saved, setSaved] = useState<Saved | null>(null);
  /** One id for the whole draft: a save whose reply was lost is never made twice, however the draft changes. */
  const requestId = useRef(newClientRequestId());
  const savedHeading = useRef<HTMLHeadingElement>(null);
  const previews = useRef<Set<string>>(new Set());
  const removed = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!getCollectorToken()) {
      history.push(collectorLoginPath(history.location.pathname + history.location.search));
    }
  }, [history]);

  // Free every preview the page made when it goes away.
  useEffect(() => {
    const made = previews.current;
    return () => {
      made.forEach((url) => URL.revokeObjectURL(url));
      made.clear();
    };
  }, []);

  useEffect(() => {
    if (saved) savedHeading.current?.focus();
  }, [saved]);

  const freePreview = (url: string) => {
    if (previews.current.delete(url)) URL.revokeObjectURL(url);
  };

  const cover = photos[0];
  const uploading = photos.some((p) => !p.url && !p.failed);
  const failed = photos.some((p) => p.failed);
  const ready = Boolean(cover?.url) && !uploading && !failed;

  const patch = (key: string, next: Partial<Photo>) =>
    setPhotos((prev) => prev.map((p) => (p.key === key ? { ...p, ...next } : p)));

  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    setError("");
    setPlanLimit(false);
    let room = QUICK_ADD_PHOTO_CAP - photos.length;
    let overCap = false;
    for (const file of Array.from(list)) {
      try {
        assertCollectorImage(file);
      } catch (err) {
        // Only photos that are accepted count towards the limit.
        setError(err instanceof Error ? err.message : "Please choose a photo.");
        continue;
      }
      if (room <= 0) {
        overCap = true;
        continue;
      }
      room -= 1;
      photoSeq += 1;
      const key = `p${photoSeq}`;
      const preview = URL.createObjectURL(file);
      previews.current.add(preview);
      setPhotos((prev) => [...prev, { key, preview, percent: 0 }]);
      uploadCollectorPhoto(file, (percent) => patch(key, { percent }))
        .then((url) => patch(key, { url, percent: 100 }))
        .catch((err) => {
          if (removed.current.has(key)) return;
          patch(key, { failed: true });
          const problem = collectorPhotoProblem(err);
          if (problem.kind === "session") {
            history.push(collectorLoginPath(history.location.pathname + history.location.search));
            return;
          }
          setError(problem.kind === "other" ? "A photo did not upload. Remove it and try again." : problem.message);
        });
    }
    if (overCap) setError(`You can add ${QUICK_ADD_PHOTO_CAP} photos here. Add more later on the piece page.`);
  };

  const removePhoto = (key: string) => {
    removed.current.add(key);
    const gone = photos.find((p) => p.key === key);
    if (gone) freePreview(gone.preview);
    const left = photos.filter((p) => p.key !== key);
    setPhotos((prev) => prev.filter((p) => p.key !== key));
    // The "did not upload" message belongs to the photo just removed.
    if (gone?.failed && !left.some((p) => p.failed)) {
      setError("");
    }
  };

  const save = async () => {
    if (!ready || saving) return;
    setSaving(true);
    setError("");
    const body: DraftBody = {
      title: title.trim() || undefined,
      coverPhotoUrl: cover.url,
      additionalImageUrls: photos.slice(1).flatMap((p) => (p.url ? [p.url] : [])),
    };
    const send = () => createExternalItem({ ...body, clientRequestId: requestId.current });
    try {
      let res;
      try {
        res = await send();
      } catch (first: unknown) {
        // The id belongs to a piece that was archived since: this is a new piece, so retry once with a new id.
        if ((first as { response?: { status?: number } })?.response?.status !== 409 ||
            serverMessage(first) !== CLIENT_REQUEST_ARCHIVED) throw first;
        requestId.current = newClientRequestId();
        res = await send();
      }
      const data = res.data?.data || {};
      let item = data.item || data;
      const id = collectorMongoId(item) || String(item.collectorItemId || "");
      // An earlier attempt of this draft had saved: bring that piece up to what the person has now.
      const replayed = Boolean(data.replayed ?? res.data?.replayed);
      const changes = replayed ? draftChanges(body, item) : null;
      if (changes) {
        try {
          const updated = await updateCollectorItem(id, changes);
          item = { ...item, ...(updated.data?.data || changes) };
        } catch (err: unknown) {
          if (isCollectorSessionError(err) || isPhotoExpiredError(err)) throw err;
          setError("Your piece is saved, but the latest name or photos are not. Press Save again.");
          return;
        }
      }
      setSaved({ id, title: item.title || title.trim() });
    } catch (err: unknown) {
      if (isCollectorSessionError(err)) {
        // Renewal already failed: sign in again and come back to this page.
        history.push(collectorLoginPath(history.location.pathname + history.location.search));
        return;
      }
      const msg = serverMessage(err);
      const atLimit = msg === PLAN_LIMIT;
      setPlanLimit(atLimit);
      setError(
        atLimit
          ? "Your plan is full, so this piece was not saved."
          : isPhotoExpiredError(err)
            ? COLLECTOR_PHOTO_EXPIRED_MESSAGE
            : readableServerMessage(msg) || "Could not save the piece. Check your internet and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const startOver = () => {
    photos.forEach((p) => {
      removed.current.add(p.key);
      freePreview(p.preview);
    });
    setPhotos([]);
    setTitle("");
    setSaved(null);
    setError("");
    setPlanLimit(false);
    requestId.current = newClientRequestId();
  };

  if (saved) {
    const piece = `/my-collection/item/${saved.id}`;
    const named = saved.title && saved.title !== UNTITLED_PIECE_TITLE;
    return (
      <MyCollectionLayout>
        <div className="mc-qa">
          <div className="mc-qa-done" role="status">
            <span className="mc-qa-done-mark" aria-hidden="true">✓</span>
            <h1 className="mc-qa-title" ref={savedHeading} tabIndex={-1}>Saved</h1>
            <p className="mc-qa-lead">
              {named ? `${saved.title} is in your collection.` : "Your piece is in your collection."} Only you can
              see it until you share or print it.
            </p>
          </div>
          <div className="mc-qa-next">
            <Link className="mc-qa-next-row" to={`${piece}?edit=1`}>
              <span className="mc-qa-next-text">
                <strong>{named ? "Add its story" : "Add a name and its story"}</strong>
                <span>Who made it and where it is from</span>
              </span>
              <span aria-hidden="true">›</span>
            </Link>
            <Link className="mc-qa-next-row" to={`${piece}?print=1`}>
              <span className="mc-qa-next-text">
                <strong>Print a placard</strong>
                <span>A small card with a QR code</span>
              </span>
              <span aria-hidden="true">›</span>
            </Link>
            <button type="button" className="mc-qa-next-row" onClick={startOver}>
              <span className="mc-qa-next-text">
                <strong>Add another piece</strong>
                <span>One photo is enough</span>
              </span>
              <span aria-hidden="true">›</span>
            </button>
          </div>
          <Button className="mc-btn-secondary mc-qa-wide" onClick={() => history.push("/my-collection")}>
            Done
          </Button>
        </div>
      </MyCollectionLayout>
    );
  }

  const photoInputs = (
    <>
      <input
        id="mc-qa-camera"
        className="visually-hidden"
        type="file"
        accept="image/*"
        capture="environment"
        aria-label="Take a photo"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        id="mc-qa-gallery"
        className="visually-hidden"
        type="file"
        accept="image/*"
        multiple
        aria-label="Choose from your photos"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </>
  );

  return (
    <MyCollectionLayout>
      <div className="mc-qa">
        <McBackLink to="/my-collection">My pieces</McBackLink>
        <h1 className="mc-qa-title">Add a piece</h1>
        {photoInputs}
        {error && (
          <Alert color="warning" className="mc-qa-alert">
            {error}
            {planLimit && (
              <>
                {" "}
                <Link to="/my-collection/upgrade">See how to add more pieces</Link>
              </>
            )}
          </Alert>
        )}

        {!cover ? (
          <>
            <p className="mc-qa-lead">Take one photo. You can add the rest later.</p>
            <label htmlFor="mc-qa-camera" className="mc-qa-camera">
              <span className="mc-qa-camera-icon" aria-hidden="true" />
              <span className="mc-qa-camera-label">Take a photo</span>
              <span className="mc-qa-camera-hint">Fit the whole piece in the frame</span>
            </label>
            <label htmlFor="mc-qa-gallery" className="mc-qa-link">
              Choose from your photos
            </label>
          </>
        ) : (
          <>
            <ul className="mc-qa-photos" aria-label="Photos of this piece">
              {photos.map((p, i) => (
                <li key={p.key} className={i === 0 ? "mc-qa-photo is-cover" : "mc-qa-photo"}>
                  <img src={p.preview} alt={i === 0 ? "Cover photo" : `Photo ${i + 1}`} />
                  {!p.url && !p.failed && (
                    <span className="mc-qa-photo-progress">
                      <McUploadProgress percent={p.percent} />
                    </span>
                  )}
                  {p.failed && <span className="mc-qa-photo-failed">Not uploaded</span>}
                  <button
                    type="button"
                    className="mc-qa-photo-remove"
                    aria-label={`Remove photo ${i + 1}`}
                    onClick={() => removePhoto(p.key)}
                  >
                    ×
                  </button>
                </li>
              ))}
              {photos.length < QUICK_ADD_PHOTO_CAP && (
                <li>
                  {/* The photo picker: phones offer the camera and the gallery from here. */}
                  <label htmlFor="mc-qa-gallery" className="mc-qa-photo-add">
                    <span aria-hidden="true">+</span>
                    Add photo
                  </label>
                </li>
              )}
            </ul>

            <label htmlFor="mc-qa-name" className="mc-qa-label">
              What is it? <span className="mc-qa-optional">(you can skip this)</span>
            </label>
            <input
              id="mc-qa-name"
              className="mc-qa-input"
              value={title}
              maxLength={200}
              placeholder="Wedding saree"
              autoComplete="off"
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  save();
                }
              }}
            />

            <div className="mc-qa-actions">
              <Button className="mc-btn-primary mc-qa-wide" onClick={save} disabled={!ready || saving}>
                {saving ? "Saving…" : failed ? "Remove the photo that failed" : !ready ? "Uploading photos…" : title.trim() ? "Save" : "Save without a name"}
              </Button>
            </div>
          </>
        )}
      </div>
    </MyCollectionLayout>
  );
};

export default AddExternalPiece;
